import { LogManager } from '@src/utility/Logger';
import {
  buildElementTimingMap,
  type ElementTiming,
  resolveSceneRange,
} from '@src/utility/sceneAnchors';

import type { Scene } from '@src/editor/models/layout/Scene';

/**
 * Structural interface for the editor - decoupled from concrete EditorStore.
 * Defines only what this service needs.
 */
export interface EditorStoreLike {
  editor: {
    timeline: {
      readonly scenes: readonly Scene[];
    };
    commandSplitScene: (sceneId: string, relativeTime: number) => boolean;
  };
}

/**
 * Structural interface for video creator store - only what we need for duration.
 */
export interface VideoCreatorStoreLike {
  duration: number;
}

export interface SplitSceneServiceDeps {
  editorStore: EditorStoreLike;
  videoCreator: VideoCreatorStoreLike;
}

/**
 * Service to split a scene at a given time.
 * If the time is over a scene, splits that scene.
 * Returns false if time is not over any scene (e.g., on the edge between scenes).
 */
export class SplitSceneService {
  private readonly _editorStore: EditorStoreLike;
  private readonly _videoCreator: VideoCreatorStoreLike;
  private readonly _logger = LogManager.getLogger('service:SplitSceneService');

  constructor(deps: SplitSceneServiceDeps) {
    this._editorStore = deps.editorStore;
    this._videoCreator = deps.videoCreator;
  }

  /**
   * Finds the scene at the given time.
   * @param time - The time to check.
   * @param elementTimings - Pre-built element timings for original video elements.
   * @returns The scene and its range, or null if no scene contains the given time.
   */
  private findSceneAtTime(
    time: number,
    elementTimings: ElementTiming[],
  ): {
    scene: Scene;
    range: { start: number; end: number };
  } | null {
    const durationSeconds = this._videoCreator.duration;
    const elementTimingMap = buildElementTimingMap(elementTimings);
    const scenes = this._editorStore.editor.timeline.scenes;

    for (const scene of scenes) {
      const range = resolveSceneRange(scene, elementTimingMap, durationSeconds);
      // Strict inequality: time must be strictly inside the scene (not at edges)
      if (time > range.start && time < range.end) {
        return { scene, range };
      }
    }

    return null;
  }

  /**
   * Splits the scene at the given time.
   *
   * @param time - The time at which to split.
   * @param elementTimings - Pre-built element timings for original video elements.
   * @returns true if a scene was split, false if time is not over any scene.
   */
  splitSceneAt(time: number, elementTimings: ElementTiming[]): boolean {
    // Try to find scene at the given time
    const sceneAtTime = this.findSceneAtTime(time, elementTimings);

    if (!sceneAtTime) {
      // Time is not over any scene - do nothing
      return false;
    }

    const { scene, range } = sceneAtTime;
    const splitTime = time - range.start;

    if (splitTime <= 0) {
      this._logger.warn(
        `Cannot split scene ${scene.id} because split time is zero`,
      );
      return false;
    }

    const success = this._editorStore.editor.commandSplitScene(
      scene.id,
      splitTime,
    );

    if (!success) {
      this._logger.warn(`Failed to split scene ${scene.id}`);
      return false;
    }

    return true;
  }
}
